import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Lock, Loader2, CheckCircle, AlertCircle } from 'lucide-react';

export const ResetPassword: React.FC = () => {
  const [canReset, setCanReset] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const checkRecoveryParams = async () => {
      try {
        // Case 1: Newer flow with authorization code (e.g., ?code=...)
        const search = new URLSearchParams(window.location.search);
        const code = search.get('code');
        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) throw error;
          setCanReset(true);
          setInitializing(false);
          return;
        }

        // Case 2: Hash fragment with access_token and type=recovery (#access_token=...&type=recovery)
        const hash = window.location.hash.startsWith('#') ? window.location.hash.substring(1) : window.location.hash;
        const params = new URLSearchParams(hash);
        const type = params.get('type');
        if (type === 'recovery') {
          setCanReset(true);
          setInitializing(false);
          return;
        }

        // Fallback: listen for auth state changes (PASSWORD_RECOVERY)
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
          if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') {
            setCanReset(true);
            setInitializing(false);
          }
        });

        // Also attempt to get current session (in case it's already set)
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          setCanReset(true);
        }
        setInitializing(false);

        return () => {
          subscription?.unsubscribe();
        };
      } catch (err) {
        console.error('Error inicializando restablecimiento:', err);
        setError('El enlace de restablecimiento no es válido o expiró. Solicitá uno nuevo.');
        setInitializing(false);
      }
    };

    checkRecoveryParams();
  }, []);

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden.');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      setMessage('¡Tu contraseña fue actualizada correctamente!');

      // After a short delay, send the user to login
      setTimeout(() => navigate('/auth'), 1200);
    } catch (err) {
      console.error('Error actualizando contraseña:', err);
      setError('No pudimos actualizar tu contraseña. Probá nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#EFF0F5] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <img
            src="https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system//Logo.svg"
            alt="Atlas logo"
            className="w-20 h-20"
          />
          <div className="flex flex-col">
            <span
              className="text-4xl font-bold tracking-wider"
              style={{
                fontFamily:
                  'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              }}
            >
              ATLAS
            </span>
            <span className="text-xs font-light tracking-[0.1em] text-gray-600">SISTEMA IA</span>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Restablecer contraseña</h2>
          <p className="text-gray-500 mb-6">
            {initializing
              ? 'Verificando enlace de restablecimiento...'
              : canReset
              ? 'Ingresá tu nueva contraseña para continuar.'
              : 'El enlace no es válido o está vencido.'}
          </p>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-start gap-2">
              <AlertCircle className="w-5 h-5 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {message && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-600 text-sm flex items-start gap-2">
              <CheckCircle className="w-5 h-5 mt-0.5" />
              <span>{message}</span>
            </div>
          )}

          {initializing ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
            </div>
          ) : canReset ? (
            <form onSubmit={handleUpdatePassword} className="space-y-4">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Nueva contraseña
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:border-transparent"
                    placeholder="••••••••"
                    required
                  />
                  <Lock className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                </div>
                <p className="text-xs text-gray-500 mt-1">Mínimo 8 caracteres.</p>
              </div>

              <div>
                <label htmlFor="confirm" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirmar contraseña
                </label>
                <div className="relative">
                  <input
                    id="confirm"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:border-transparent"
                    placeholder="••••••••"
                    required
                  />
                  <Lock className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-md shadow-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Guardar nueva contraseña
                    <CheckCircle className="w-5 h-5" />
                  </>
                )}
              </button>

              <div className="text-center text-sm">
                <button
                  type="button"
                  onClick={() => navigate('/auth')}
                  className="text-blue-600 hover:text-blue-700"
                >
                  Volver a iniciar sesión
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              <button
                type="button"
                onClick={() => navigate('/auth')}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-md shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Solicitar un nuevo enlace
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
